/**
 * Hooks de dados da tela «Risco por Ação» (Motor Neutro D2 · casa de config da A3).
 *
 * Consome o módulo `action-risk-category` do Engine:
 *   - GET    /v1/action-risk-categories/resolved?vertical=  · gate `action-risk-category-read`
 *   - POST   /v1/action-risk-categories/preview             · gate `action-risk-category-read`
 *   - POST   /v1/action-risk-categories                     · gate `action-risk-category-author`
 *   - PATCH  /v1/action-risk-categories/:id   (If-Match)    · gate `action-risk-category-author`
 *   - DELETE /v1/action-risk-categories/:id   (If-Match)    · gate `action-risk-category-author`
 *   - POST   /v1/action-risk-categories/:id/approve (If-Match) · gate `-platform-approve`
 *
 * A query é GATEADA no `enabled` por capability — sem `action-risk-category-read` o fetch nem
 * dispara («gatear o fetch, não só o render»). O dry-run também: é a MESMA cap do GET.
 *
 * `staleTime: 0` de propósito (lição da casa): esta tela mostra o veredito de um gate de
 * segurança; cache stale aqui faria o operador ler um piso que já mudou. O custo é baixo (a
 * leitura é uma projeção Tier S no servidor).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';

import type {
  CreateActionRiskBody,
  PreviewActionRiskBody,
  PreviewResultView,
  ResolvedCatalogView,
  TenantOverrideView,
  UpdateActionRiskBody,
} from './types';
import type { UseMutationResult } from '@tanstack/react-query';

const RESOLVED_QUERY_KEY = 'action-risk-categories-resolved';

export function useResolvedActionRiskCategories(vertical: string | null): {
  data: ResolvedCatalogView | null;
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('action-risk-category-read');
  const query = useQuery({
    queryKey: [RESOLVED_QUERY_KEY, vertical],
    queryFn: () => api.getResolvedActionRiskCategories(vertical),
    enabled: canRead,
    staleTime: 0,
    retry: 1,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

/** Dry-run — NÃO persiste. É o que dá valor real ao operador antes de autorar. */
export function usePreviewActionRiskCategory(): UseMutationResult<
  PreviewResultView,
  unknown,
  PreviewActionRiskBody
> {
  return useMutation({
    mutationFn: (body: PreviewActionRiskBody) => api.previewActionRiskCategory(body),
  });
}

function useInvalidateResolved(): () => void {
  const queryClient = useQueryClient();
  // invalida TODAS as chaves de vertical — a row do tenant é vertical-agnóstica, então
  // autorar sob um vertical muda a leitura de todos (espelha a invalidação por PREFIXO que a
  // D2 fez no ConfigCache do Engine; guardá-la só de um lado deixaria o outro stale).
  return () => void queryClient.invalidateQueries({ queryKey: [RESOLVED_QUERY_KEY] });
}

/**
 * ★ Fable D2 I3 — invalida também no ERRO.
 *
 * O caminho de 409 (versão divergente) precisa recarregar: sem isso o operador fica preso num
 * loop de conflito com a `version` stale em mão, e a cópia «Recarregamos a lista» — que o toast
 * exibe — seria **mentira** (o `refetchOnWindowFocus:false` global garante que nada recarregaria
 * sozinho). Invalidar em qualquer desfecho é fail-safe: no sucesso já era necessário, no erro
 * traz a versão nova que torna a 2ª tentativa possível.
 */
function useSettleResolved(): () => void {
  return useInvalidateResolved();
}

export function useCreateActionRiskCategory(): UseMutationResult<
  TenantOverrideView,
  unknown,
  CreateActionRiskBody
> {
  const settle = useSettleResolved();
  return useMutation({
    mutationFn: (body: CreateActionRiskBody) => api.createActionRiskCategory(body),
    onSettled: settle,
  });
}

export function useUpdateActionRiskCategory(): UseMutationResult<
  TenantOverrideView,
  unknown,
  { id: string; expectedVersion: number; body: UpdateActionRiskBody }
> {
  const settle = useSettleResolved();
  return useMutation({
    mutationFn: ({ id, expectedVersion, body }) =>
      api.updateActionRiskCategory(id, expectedVersion, body),
    onSettled: settle,
  });
}

export function useDeleteActionRiskCategory(): UseMutationResult<
  void,
  unknown,
  { id: string; expectedVersion: number }
> {
  const settle = useSettleResolved();
  return useMutation({
    mutationFn: ({ id, expectedVersion }) => api.deleteActionRiskCategory(id, expectedVersion),
    onSettled: settle,
  });
}

export function useApproveActionRiskCategory(): UseMutationResult<
  TenantOverrideView,
  unknown,
  { id: string; expectedVersion: number }
> {
  const settle = useSettleResolved();
  return useMutation({
    mutationFn: ({ id, expectedVersion }) => api.approveActionRiskCategory(id, expectedVersion),
    onSettled: settle,
  });
}
